// Date field kit — shadcn Calendar-in-Popover composition replacing the native
// `<input type="date">` (dev decision 2026-07-03: browser-chrome picker
// rejected; whole-field click must open the calendar).
//
// - APFormDate (RHF, single): editable text input (typed 'YYYY-MM-DD' entry is
//   a first-class path) + calendar assist. Wire value unchanged:
//   'YYYY-MM-DD' | null (Rails `date` columns, zero drift from the old field).
// - APDateRange (standalone, controlled): one trigger showing `from – to`,
//   range calendar, clear affordance. Commits onChange only when BOTH ends are
//   picked (half-ranges never leak to consumers). Filter-panel flavor — the
//   second deliberate standalone next to APMultiSelect.
// - APFormDateRange (RHF binding of APDateRange): form value is
//   `{ from: 'YYYY-MM-DD' | null, to: 'YYYY-MM-DD' | null }` under one name;
//   consumers map to their two wire params at the page edge.
import { CalendarMinimalisticLinear, CloseCircleLinear } from '@assessprep/icons/solar';
import dayjs from 'dayjs';
import * as React from 'react';
import type { DateRange, Matcher } from 'react-day-picker';
import { useFormContext, type FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Calendar } from '@/components/ui/calendar';
import { FormControl, FormField } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { useIsMobile } from '@/components/ui/use-mobile';
import { cn } from '@/lib/cn';
import { APFieldShell, fieldTestId, type APFieldBaseProps } from './Fields';

export const DATE_WIRE_FORMAT = 'YYYY-MM-DD';
const WIRE_FORMAT = DATE_WIRE_FORMAT;

// Kit-internal exports below (toDate … focusDayGrid): APFormDateTime rebuilds
// the same typed-entry + calendar wiring inside its combined popover.
export const toDate = (wire: string | null | undefined): Date | undefined => {
  if (!wire) return undefined;
  const d = dayjs(wire);
  return d.isValid() ? d.toDate() : undefined;
};

const toApiValue = (date: Date): string => dayjs(date).format(WIRE_FORMAT);

/** Strict typed-entry parse: exact YYYY-MM-DD that round-trips (rejects
 *  overflow dates like 2026-02-31, which dayjs would silently roll forward). */
export const parseTyped = (text: string): string | null => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return null;
  const d = dayjs(text);
  return d.isValid() && d.format(WIRE_FORMAT) === text ? text : null;
};

/** min/max ('YYYY-MM-DD') → react-day-picker disabled matchers. */
export const boundsMatchers = (min?: string, max?: string): Matcher[] | undefined => {
  const matchers: Matcher[] = [];
  const minDate = toDate(min);
  const maxDate = toDate(max);
  if (minDate) matchers.push({ before: minDate });
  if (maxDate) matchers.push({ after: maxDate });
  return matchers.length > 0 ? matchers : undefined;
};

// Lexicographic compare is date order for zero-padded YYYY-MM-DD.
export const inBounds = (wire: string, min?: string, max?: string): boolean =>
  (!min || wire >= min) && (!max || wire <= max);

export const assignRef = <T,>(ref: React.Ref<T> | undefined, node: T | null) => {
  if (typeof ref === 'function') ref(node);
  else if (ref) (ref as React.MutableRefObject<T | null>).current = node;
};

/** Focus RDP's roving-tabindex target (selected day, else today) so native
 *  arrow-key grid navigation takes over. RDP syncs its internal focus state
 *  from the button's own focus event. */
export const focusDayGrid = (calendarRoot: HTMLElement | null) => {
  calendarRoot?.querySelector<HTMLButtonElement>('button[data-day][tabindex="0"]')?.focus();
};

export interface SingleDateControlProps {
  value: string | null;
  onChange: (next: string | null) => void;
  disabled?: boolean;
  min?: string;
  max?: string;
  floating: boolean;
  placeholder?: string;
  testId: string;
  /** RHF `field.ref` — lets `shouldFocusError` land on this input. */
  controlRef?: React.Ref<HTMLInputElement>;
  /** Cross-field flag: force aria-invalid when a sibling field owns the error. */
  invalid?: boolean;
  /** false = emptying the input is ignored (v3 `allowClear={false}`). */
  clearable?: boolean;
}

/** RHF-context control: text input (typed entry) + whole-field-click popover
 *  calendar. Invalid / out-of-range typed input never reaches the form value;
 *  blur re-syncs the display to the last committed value. (APFormDateTime once
 *  composed this control; since P2-54 it owns a combined-popover variant and
 *  shares only the helpers above.) */
export const SingleDateControl = ({
  value,
  onChange,
  disabled,
  min,
  max,
  floating,
  placeholder,
  testId,
  controlRef,
  invalid,
  clearable = true,
}: SingleDateControlProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [text, setText] = React.useState(value ?? '');
  const [month, setMonth] = React.useState<Date | undefined>(() => toDate(value));
  const inputRef = React.useRef<HTMLInputElement>(null);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const calendarRef = React.useRef<HTMLDivElement>(null);
  // Keyboard opens hand focus to the day grid (and get it back on close);
  // pointer opens leave typing focus on the input. Ref, not state: the close
  // callbacks fire after a state flush and must read the latest value.
  const gridOwnsFocusRef = React.useRef(false);

  // External writes (hydration, reset, calendar select) re-sync the display.
  React.useEffect(() => {
    setText(value ?? '');
  }, [value]);

  const openPopover = (source: 'pointer' | 'keyboard' = 'pointer') => {
    if (disabled) return;
    gridOwnsFocusRef.current = source === 'keyboard';
    setMonth(toDate(value) ?? new Date());
    setOpen(true);
  };

  const handleTextChange = (raw: string) => {
    // Not clearable: ignore an emptying keystroke; the controlled input reverts
    // to the committed value (v3 allowClear={false} — MB-linked close date).
    if (raw === '' && !clearable) return;
    setText(raw);
    if (raw === '') {
      onChange(null);
      return;
    }
    const wire = parseTyped(raw);
    if (wire && inBounds(wire, min, max)) {
      onChange(wire);
      setMonth(toDate(wire));
    }
  };

  // Visible clear affordance (mirrors APDateRange): shown only while a value is
  // committed and clearing is allowed. Refocuses the input so keyboard users
  // aren't dropped to <body> when the button unmounts.
  const showClear = clearable && !!value && !disabled;
  const clear = () => {
    onChange(null);
    setText('');
    inputRef.current?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Space is safe to intercept: the wire format has none.
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
      event.preventDefault();
      if (open && event.key === 'Enter') {
        setOpen(false);
      } else if (open && event.key === 'ArrowDown') {
        // Already open (e.g. via click) — ArrowDown steps into the grid.
        gridOwnsFocusRef.current = true;
        focusDayGrid(calendarRef.current);
      } else {
        openPopover('keyboard');
      }
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        {/* The input is nested (popover anchor), so the floating label tracks
            via `group` mode: data-placeholder = emptiness, data-state = open.
            min-w-36: floor = 10 wire chars + ps-3/pe-9 icon reserve — below it
            the leading digit clips under the icon in RTL (AI-9). */}
        <div ref={anchorRef} className="relative min-w-36" data-state={open ? 'open' : 'closed'}>
          <FormControl>
            <Input
              ref={(node) => {
                inputRef.current = node;
                assignRef(controlRef, node);
              }}
              value={text}
              onChange={(event) => handleTextChange(event.target.value)}
              onClick={() => openPopover('pointer')}
              onKeyDown={handleKeyDown}
              onBlur={() => setText(value ?? '')}
              placeholder={floating ? ' ' : placeholder ?? WIRE_FORMAT}
              disabled={disabled}
              autoComplete="off"
              role="combobox"
              aria-expanded={open}
              aria-haspopup="dialog"
              aria-label={floating ? undefined : t('APDateField.open_calendar')}
              data-placeholder={text === '' ? '' : undefined}
              className={cn(
                showClear ? 'pe-14' : 'pe-9',
                'tabular-nums',
                floating && 'placeholder:text-transparent'
              )}
              data-testid={`${testId}-input`}
              // Conditional spread: `aria-invalid={undefined}` would override
              // FormControl's slot-provided value (own error) instead of
              // deferring to it.
              {...(invalid ? { 'aria-invalid': true } : {})}
            />
          </FormControl>
          {showClear && (
            <button
              type="button"
              onClick={clear}
              aria-label={t('APDateField.clear_date')}
              className="absolute end-8 top-1/2 -translate-y-1/2 rounded-sm text-muted-foreground outline-none hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ap-ring-a50"
              data-testid={`${testId}-clear`}
            >
              <CloseCircleLinear aria-hidden="true" className="size-4" />
            </button>
          )}
          <CalendarMinimalisticLinear
            aria-hidden="true"
            className={cn(
              'pointer-events-none absolute end-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground',
              disabled && 'opacity-50'
            )}
          />
        </div>
      </PopoverAnchor>
      <PopoverContent
        className="w-auto p-0"
        align="start"
        onOpenAutoFocus={(event) => {
          // Radix must never grab focus itself; keyboard opens (AE-7) move it
          // onto the selected/today day so arrow navigation works, pointer
          // opens keep typing focus on the input.
          event.preventDefault();
          if (gridOwnsFocusRef.current) focusDayGrid(calendarRef.current);
        }}
        onCloseAutoFocus={(event) => {
          // No PopoverTrigger exists (anchor composition), so Radix would drop
          // focus to <body> — return it to the input when the grid held it.
          event.preventDefault();
          if (gridOwnsFocusRef.current) inputRef.current?.focus();
          gridOwnsFocusRef.current = false;
        }}
        onInteractOutside={(event) => {
          // Clicking the field itself must not bounce the popover closed-open.
          const target = event.target as Node | null;
          if (target && anchorRef.current?.contains(target)) {
            event.preventDefault();
            return;
          }
          // Pointer dismissal — focus follows the pointer, never steal it back.
          gridOwnsFocusRef.current = false;
        }}
        data-testid={`${testId}-calendar`}
      >
        <Calendar
          ref={calendarRef}
          mode="single"
          required
          selected={toDate(value)}
          onSelect={(date) => {
            const wire = toApiValue(date);
            setText(wire);
            onChange(wire);
            setOpen(false);
            inputRef.current?.focus();
          }}
          month={month}
          onMonthChange={setMonth}
          disabled={boundsMatchers(min, max)}
        />
      </PopoverContent>
    </Popover>
  );
};

/** Single date field (RHF) — same name + props surface as the retired native
 *  implementation; only the UI internals changed. Form value stays
 *  `'YYYY-MM-DD' | null`. */
export function APFormDate<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  description,
  rules,
  variant = 'floating',
  required,
  disabled,
  className,
  min,
  max,
  'data-testid': testId,
}: APFieldBaseProps<TFieldValues> & { min?: string; max?: string }) {
  const { control } = useFormContext<TFieldValues>();
  const floating = variant === 'floating' && !!label;
  return (
    <FormField
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => (
        <APFieldShell
          label={label}
          description={description}
          className={className}
          testId={fieldTestId(testId, name)}
          variant={variant}
          required={required ?? !!rules?.required}
          floatingMode="group"
        >
          <SingleDateControl
            value={(field.value as string | null | undefined) ?? null}
            onChange={field.onChange}
            controlRef={field.ref}
            disabled={disabled}
            min={min}
            max={max}
            floating={floating}
            testId={fieldTestId(testId, name)}
          />
        </APFieldShell>
      )}
    />
  );
}

export interface APDateRangeValue {
  from: string | null;
  to: string | null;
}

/** Quick-pick rail entry (v3 RangePicker `presets` parity). `range` is
 *  computed at click time so relative ranges stay fresh across midnight. */
export interface APDateRangePreset {
  key: string;
  /** Already-translated label. */
  label: string;
  range: () => APDateRangeValue;
}

export interface APDateRangeProps {
  value: APDateRangeValue;
  onChange: (next: APDateRangeValue) => void;
  /** Shown when empty in non-floating contexts (filter panels). */
  placeholder?: string;
  disabled?: boolean;
  min?: string;
  max?: string;
  /** Clear affordance (default true). */
  clearable?: boolean;
  numberOfMonths?: number;
  /** Quick-pick rail beside the calendar; a preset commits + closes. */
  presets?: APDateRangePreset[];
  /** Set by APFormDateRange: the floating label occupies the resting slot. */
  floatingLabel?: boolean;
  className?: string;
  'data-testid'?: string;
}

/** Standalone controlled range field — one trigger showing `from – to`.
 *  onChange fires only with a complete range (or a full clear); consumers map
 *  the two ends to their own wire params at the page edge. */
export const APDateRange = React.forwardRef<HTMLButtonElement, APDateRangeProps>(
  (
    {
      value,
      onChange,
      placeholder,
      disabled,
      min,
      max,
      clearable = true,
      numberOfMonths = 2,
      presets,
      floatingLabel,
      className,
      'data-testid': testId,
      ...rest
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [open, setOpen] = React.useState(false);
    const [pending, setPending] = React.useState<DateRange | undefined>();
    // Two-click semantics: react-day-picker's first range click already
    // returns a complete same-day range, so track the first pick ourselves.
    const firstPickRef = React.useRef<Date | null>(null);
    const anchorRef = React.useRef<HTMLDivElement>(null);
    const triggerRef = React.useRef<HTMLButtonElement>(null);
    const calendarRef = React.useRef<HTMLDivElement>(null);
    // Same keyboard-focus contract as SingleDateControl (AE-7).
    const gridOwnsFocusRef = React.useRef(false);
    // Two months overflow narrow viewports (390px) — collapse to one below md.
    const isMobile = useIsMobile();
    const monthsShown = isMobile ? 1 : numberOfMonths;

    const committed: DateRange | undefined = value.from
      ? { from: toDate(value.from), to: toDate(value.to) }
      : undefined;
    const selected = open ? pending : committed;

    const hasValue = !!value.from || !!value.to;
    const display = open
      ? pending?.from
        ? `${toApiValue(pending.from)} – ${pending.to ? toApiValue(pending.to) : ''}`
        : ''
      : hasValue
      ? `${value.from ?? ''} – ${value.to ?? ''}`
      : '';

    const handleOpenChange = (next: boolean) => {
      setOpen(next);
      firstPickRef.current = null;
      setPending(next ? committed : undefined); // half-ranges never commit
    };

    const handleSelect = (_range: DateRange | undefined, day: Date) => {
      const first = firstPickRef.current;
      if (!first) {
        firstPickRef.current = day;
        setPending({ from: day, to: undefined });
        return;
      }
      const [from, to] = first <= day ? [first, day] : [day, first];
      onChange({ from: toApiValue(from), to: toApiValue(to) });
      firstPickRef.current = null;
      setPending(undefined);
      setOpen(false);
    };

    const clear = () => {
      onChange({ from: null, to: null });
      setPending(undefined);
    };

    const handlePreset = (preset: APDateRangePreset) => {
      onChange(preset.range());
      firstPickRef.current = null;
      setPending(undefined);
      setOpen(false);
    };

    return (
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverAnchor asChild>
          <div ref={anchorRef} className={cn('relative', className)}>
            <button
              ref={(node) => {
                triggerRef.current = node;
                assignRef(ref, node);
              }}
              type="button"
              onClick={(event) => {
                // Keyboard activation (Enter/Space) fires click with detail 0.
                gridOwnsFocusRef.current = !open && event.detail === 0;
                handleOpenChange(!open);
              }}
              onKeyDown={(event) => {
                if (event.key !== 'ArrowDown') return;
                event.preventDefault();
                gridOwnsFocusRef.current = true;
                if (open) focusDayGrid(calendarRef.current);
                else handleOpenChange(true);
              }}
              disabled={disabled}
              role="combobox"
              aria-expanded={open}
              aria-haspopup="dialog"
              aria-label={t('APDateField.open_calendar')}
              data-placeholder={hasValue || open ? undefined : ''}
              className={cn(
                // Input-box look (mirrors ui/input.tsx) so the trigger sits
                // flush with sibling fields.
                'flex h-9 w-full min-w-0 items-center rounded-md border border-input bg-transparent px-3 py-1 text-start text-base shadow-xs transition-[color,box-shadow] outline-none md:text-sm dark:bg-ap-input-a30',
                'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ap-ring-a50',
                'aria-invalid:border-destructive aria-invalid:ring-ap-destructive-a20 dark:aria-invalid:ring-ap-destructive-a40',
                'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
                clearable && hasValue && !disabled ? 'pe-14' : 'pe-9'
              )}
              data-testid={testId}
              {...rest}
            >
              {/* dir="ltr": the value is numeric `from – to`; without it the
                  bidi algorithm can reorder the two dates under RTL. */}
              <span
                dir={display ? 'ltr' : undefined}
                className={cn('truncate tabular-nums', !display && 'text-muted-foreground')}
              >
                {display || (floatingLabel ? '' : placeholder ?? '')}
              </span>
            </button>
            {clearable && hasValue && !disabled && (
              <button
                type="button"
                onClick={clear}
                aria-label={t('APDateField.clear')}
                className="absolute end-8 top-1/2 -translate-y-1/2 rounded-sm text-muted-foreground outline-none hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ap-ring-a50"
                data-testid={testId ? `${testId}-clear` : undefined}
              >
                <CloseCircleLinear aria-hidden="true" className="size-4" />
              </button>
            )}
            <CalendarMinimalisticLinear
              aria-hidden="true"
              className="pointer-events-none absolute end-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            />
          </div>
        </PopoverAnchor>
        <PopoverContent
          className="w-auto p-0"
          align="start"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            if (gridOwnsFocusRef.current) focusDayGrid(calendarRef.current);
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (gridOwnsFocusRef.current) triggerRef.current?.focus();
            gridOwnsFocusRef.current = false;
          }}
          onInteractOutside={(event) => {
            const target = event.target as Node | null;
            if (target && anchorRef.current?.contains(target)) {
              event.preventDefault();
              return;
            }
            gridOwnsFocusRef.current = false;
          }}
          data-testid={testId ? `${testId}-calendar` : undefined}
        >
          {/* Presets rail: column beside the calendar; row-wrap above it on
              mobile so two months + rail never overflow 390px. */}
          <div className="flex max-md:flex-col">
            {presets && presets.length > 0 && (
              <div
                className="flex flex-col gap-0.5 border-e p-2 max-md:flex-row max-md:flex-wrap max-md:border-e-0 max-md:border-b"
                data-testid={testId ? `${testId}-presets` : undefined}
              >
                {presets.map((preset) => (
                  <button
                    key={preset.key}
                    type="button"
                    onClick={() => handlePreset(preset)}
                    className="rounded-sm px-2 py-1 text-start text-sm text-foreground outline-none hover:bg-muted focus-visible:ring-[3px] focus-visible:ring-ap-ring-a50"
                    data-testid={testId ? `${testId}-preset-${preset.key}` : undefined}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            )}
            <Calendar
              ref={calendarRef}
              mode="range"
              selected={selected}
              onSelect={handleSelect}
              defaultMonth={selected?.from ?? toDate(max) ?? new Date()}
              numberOfMonths={monthsShown}
              disabled={boundsMatchers(min, max)}
            />
          </div>
        </PopoverContent>
      </Popover>
    );
  }
);
APDateRange.displayName = 'APDateRange';

/** Range field (RHF) — form value is an {@link APDateRangeValue} under one
 *  name. Floating label via the `group` mode (`data-placeholder` tracking,
 *  same contract as the Select/MultiSelect triggers). */
export function APFormDateRange<TFieldValues extends FieldValues = FieldValues>({
  name,
  label,
  description,
  rules,
  variant = 'floating',
  required,
  disabled,
  className,
  min,
  max,
  clearable,
  numberOfMonths,
  placeholder,
  'data-testid': testId,
}: APFieldBaseProps<TFieldValues> & {
  min?: string;
  max?: string;
  clearable?: boolean;
  numberOfMonths?: number;
  placeholder?: string;
}) {
  const { control } = useFormContext<TFieldValues>();
  const floating = variant === 'floating' && !!label;
  return (
    <FormField
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => (
        <APFieldShell
          label={label}
          description={description}
          className={className}
          testId={fieldTestId(testId, name)}
          variant={variant}
          required={required ?? !!rules?.required}
          floatingMode="group"
        >
          <FormControl>
            <APDateRange
              ref={field.ref}
              value={{
                from: (field.value as APDateRangeValue | undefined)?.from ?? null,
                to: (field.value as APDateRangeValue | undefined)?.to ?? null,
              }}
              onChange={field.onChange}
              disabled={disabled}
              min={min}
              max={max}
              clearable={clearable}
              numberOfMonths={numberOfMonths}
              placeholder={floating ? undefined : placeholder}
              floatingLabel={floating}
              data-testid={`${fieldTestId(testId, name)}-trigger`}
            />
          </FormControl>
        </APFieldShell>
      )}
    />
  );
}
